/// <amd-module name="typeahead/core/presentation/SuggestionRendererUtils" />

import {locationIcon} from "typeahead/core/geolocation/GeolocationIcon";
import {IEssSuggestionResult, ISuggestionResult} from "typeahead/core/Suggestion";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export class SuggestionRendererUtils {
    /*
        al refers to alias, e.g for New York it is "Big Apple".
        ap refers to the airport name string, e.g. "(NYC - All Airports)"
        the point of the tags is:
        1. tech debt: have a better way to parse data from the server rather than using paren position. in the future we
        would ideally have all parsing based on tags rather than position.
        2. support flights variant 2: so we can distinguish ap and al from each other (is in a different order depending
        on serverside bucketing and may have "extra text" in between)
        Also Refer -
        * https://eiwork.mingle.thoughtworks.com/projects/ewe_discovery_services/cards/3534
        * https://eiwork.mingle.thoughtworks.com/projects/ewe_discovery_services/cards/6135
        * https://jira.expedia.biz/browse/ECOMSVC-2412
        * https://jira.expedia.biz/browse/ECOMSVC-2936
    */
    public static alOpenTag: string = "<al>";
    public static alCloseTag: string = "</al>";
    public static apOpenTag: string = "<ap>";
    public static apCloseTag: string = "</ap>";

    public static getHtml(suggestionResult: ISuggestionResult): string {
        function appendDistanceForNearbyAirportIfApplicable() {
            let html = suggestionResult.regionNames.displayName;
            // This feature is currently limited to en_GB and en_US because it is not yet localized. Proper localization
            // should translate "2 miles away" to "2 millas de distancia" for es_US and "3 km entfernt" for de_DE.
            let essSuggestionResult: IEssSuggestionResult = <IEssSuggestionResult> suggestionResult;
            let localizedNearbyDistance: string = essSuggestionResult.hierarchyInfo &&
            essSuggestionResult.hierarchyInfo.nearby ? essSuggestionResult.hierarchyInfo.nearby.locDist : undefined;
            if (localizedNearbyDistance) {
                html = html + " " + localizedNearbyDistance;
            }
            return html;
        }

        function makeIconHtml() {
            let html;
            if (suggestionResult.icon === "geolocation") {
                html = `<span data-tid="typeahead-icon` + suggestionResult.index +
                    `" class="icon" aria-hidden="true">` +
                    `<img alt="" style="height:1em;width:1em;vertical-align:middle;" ` +
                    `src='` + locationIcon + `'/>` +
                    `</span>`;
            } else if (suggestionResult.icon === "none") {
                html = `<span data-tid="typeahead-icon`
                    + suggestionResult.index
                    + `" aria-hidden="true"></span>`;
            } else {
                html = `<span data-tid="typeahead-icon`
                    + suggestionResult.index
                    + `" class="icon" aria-hidden="true">
                    <svg class="uitk-icon-svg">
                        <use xlink:href="#uitk-icon-` + suggestionResult.icon + `"/>
                    </svg>
                    </span>`;
            }
            return html;
        }

        function convertMainToMultiLineHtml(textHtml) {
            let primaryText: string = "";
            let bottomText: string = "";
            let extraText: string = "";
            let openParenIndex: number = textHtml.indexOf("(");
            let closeParenIndex: number = textHtml.indexOf(")");
            let tla;
            let rest;

            /* main suggestions should always have parentheses */
            if (openParenIndex !== -1 && closeParenIndex !== -1) {
                primaryText = textHtml.substring(0, openParenIndex);
                bottomText = textHtml.substring(openParenIndex + 1, closeParenIndex);
                let dashIndex: number = bottomText.indexOf("-");
                if (dashIndex !== -1) {
                    tla = bottomText.substring(0, dashIndex).replace(/&nbsp;/i, "").trim();
                    rest = bottomText.substring(dashIndex + 1, bottomText.length);
                } else {
                    tla = bottomText;
                    rest = "";
                }
                bottomText = "(<B>" + tla + "</B>)" + rest;
                if (closeParenIndex + 1 < textHtml.length) {
                    extraText = textHtml.substring(closeParenIndex + 1, textHtml.length);
                }
            } else {
                primaryText = textHtml;
            }
            return SuggestionRendererUtils.createMultiLineHtml(primaryText, extraText, bottomText);
        }

        function convertChildToMultiLineHtml(textHtml) {
            let dashIndex = textHtml.indexOf("-");
            if (dashIndex !== -1) {
                let tla = textHtml.substring(0, dashIndex).replace(/&nbsp;/i, "").trim();
                let rest = textHtml.substring(dashIndex + 1, textHtml.length);

                return "(<B>" + tla + "</B>)" + rest;
            }
            // Handling the case where hypen is not available after TLA in the ESS display text (example - YQG, Windsor)
            /*
             else {
             var result = new RegExp("^([A-Z]{3}),(.*)").exec(textHtml);
             if (result && result.length && result[1] && result[2]) {
             return "(<B>" + result[1] + "</B>)" + result[2];
             }
             }*/
            return textHtml;
        }

        function getMercuryWizardTab(): string {
            return $("li.tab:visible").children("button.on").data("lob");
        }

        function isFlightSearchResultsPage(): boolean {
            return "Flights.Search" === TypeaheadUtils.getClientId();
        }

        function doMultiline(): boolean {
            return ("flight" === getMercuryWizardTab() || isFlightSearchResultsPage());
        }

        function showHotelAddress(): boolean {
            return ("LX.Checkout" === TypeaheadUtils.getClientId() && suggestionResult.type !== "HOTELNOTPRESENT") ;
        }

        function formatText(html) {
            let result: string = html;
            if (doMultiline()) {
                if (suggestionResult.showAsHierarchy) {
                    result = convertChildToMultiLineHtml(html);
                } else {
                    // new logic occurs only when #24512 (tag scream test) & #24513 (flights display alias)
                    // are turned on, should be separate from existing logic in case of rollback / turn off
                    if (SuggestionRendererUtils.isApAlTagged(html)) {
                        result = SuggestionRendererUtils.convertApAlTaggedMainToMultiLineHtml(html);
                    } else {
                        result = convertMainToMultiLineHtml(html);
                    }
                }
            }
            return result;
        }

        // TODO: reusing doMultiline(), convertMainToMultiLineHtml()
        // and refactoring formatTextForLXCheckout() is pending

        function formatTextForLXCheckout() {
            let hotelAddressHtml: string;
            if ((suggestionResult as IEssSuggestionResult) && (suggestionResult as IEssSuggestionResult).hotelAddress) {
                hotelAddressHtml = (suggestionResult as IEssSuggestionResult).hotelAddress.street + ", " +
                    (suggestionResult as IEssSuggestionResult).hotelAddress.city + ", "
                    + (suggestionResult as IEssSuggestionResult).hotelAddress.province;
            }

            let hotelAddressSpanStyle: string = "display:block;" +
            "height:19px;" +
            "font-size:12px;" +
            "line-height:1.5;" +
            "color:#757575;";

            let result = `<span style="position:relative;bottom:0.6em">` + iconHtml + "</span>" +
            `<div style="display:inline-block;">
            <span class="text">` + formatText(textHtml) + "</span>" +
            `<span class="text" style=` + hotelAddressSpanStyle + ">" + hotelAddressHtml + "</span></div>" +
            (suggestionResult.type === "LIST" ? SuggestionRendererUtils.toggleIconHtml : "");

            return result;
        }

        function makeIconAndTextHtml(hotelAddressDisplayed) {
            let html;
            if (hotelAddressDisplayed) {
                html = formatTextForLXCheckout();
            } else {
                html = iconHtml + `<span class="text">` + formatText(textHtml) + "</span>" +
                (suggestionResult.type === "LIST" ? SuggestionRendererUtils.toggleIconHtml : "");
            }
            return html;
        }

        let iconHtml = makeIconHtml();
        let textHtml = suggestionResult.regionNames.displayName;
        if (suggestionResult.showAsHierarchy) {
            textHtml = appendDistanceForNearbyAirportIfApplicable();
        }

        let iconAndTextHtml = makeIconAndTextHtml(showHotelAddress());
        return suggestionResult.showAsHierarchy ? (`<div class="child-suggestion" style="padding-left:` +
            ("none" === suggestionResult.icon ? `12px` : `1.88235em`) +
            `; width: 100%;">` + iconAndTextHtml + `</div>`) : iconAndTextHtml;
    }

    // expected input is "<ap>...</ap>". output depends on rearrange but should still be tagged
    public static formatApText(apText: string, rearrange: boolean): string {
        if (apText.indexOf(SuggestionRendererUtils.apOpenTag) !== -1) {
            apText = apText.replace(SuggestionRendererUtils.apOpenTag
                + "(", "").replace(")" + SuggestionRendererUtils.apCloseTag, "");

            let dashIndex: number = apText.indexOf("-");
            let tla = "";
            let rest = "";

            if (dashIndex !== -1) {
                tla = apText.substring(0, dashIndex).trim();
                rest = apText.substring(dashIndex + 1, apText.length);
            } else {
                tla = apText;
                rest = "";
            }

            if (rearrange) {    // output: "(<B>NYC</B>) - All Airports"
                apText = "(<B>" + tla + "</B>)" + rest;
            } else {            // output: "(<B>NYC</B> - All Airports)"
                apText = "(" + apText.replace(tla, "<B>" + tla + "</B>") + ")";
            }

            apText = SuggestionRendererUtils.apOpenTag + apText + SuggestionRendererUtils.apCloseTag;
        }
        return apText;
    }

    public static convertApAlTaggedMainToMultiLineHtml(textHtml) {
        let apOpenStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.apOpenTag);
        let apCloseStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.apCloseTag);
        let alOpenStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.alOpenTag);
        let alCloseStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.alCloseTag);

        let isApTagFirst: boolean = apOpenStartIndex < alOpenStartIndex;
        let leadingText: string = textHtml.substring(0, isApTagFirst ? apOpenStartIndex : alOpenStartIndex);
        let extraText = ""; // if present, seems to only be the near string
        // textHtml element ordering changes depending on serverside bucket
        if (isApTagFirst) {
            extraText = textHtml.substring(
                apCloseStartIndex + SuggestionRendererUtils.apCloseTag.length, alOpenStartIndex);
        } else {
            extraText = textHtml.substring(apCloseStartIndex +
                SuggestionRendererUtils.apCloseTag.length, textHtml.length);
        }

        let apText: string = textHtml.substring(
            apOpenStartIndex, apCloseStartIndex + SuggestionRendererUtils.apCloseTag.length);
        let alText: string = textHtml.substring(
            alOpenStartIndex, alCloseStartIndex + SuggestionRendererUtils.alCloseTag.length);

        let result: string = textHtml;
        if (isApTagFirst) {
            result = this.createMultiLineHtmlWrapped(leadingText + extraText,
                this.formatApText(apText, false), this.addAlVariantTwoStyleToTags(alText));
        } else {
            result = this.createMultiLineHtmlWrapped(leadingText + alText, extraText,
                this.formatApText(apText, true));
        }
        return result;
    }

    // it looks like currently there's no custom css for uta. this is a temporary workaround
    public static addAlVariantTwoStyleToTags(textHtml) {
        if (this.isAlTagged(textHtml)) {
            let styledAl: string = `<al style="font-weight:bold;font-size:12px;color:black">`;
            textHtml = textHtml.replace("(", "").replace(")", "");
            textHtml = textHtml.replace(SuggestionRendererUtils.alOpenTag, styledAl);
        }
        return textHtml;
    }

    public static isAlTagged(html) {
        return html.indexOf(SuggestionRendererUtils.alOpenTag) !== -1
            && html.indexOf(SuggestionRendererUtils.alCloseTag) !== -1;
    }

    public static isApTagged(html) {
        return html.indexOf(SuggestionRendererUtils.apOpenTag) !== -1
            && html.indexOf(SuggestionRendererUtils.apCloseTag) !== -1;
    }

    public static isApAlTagged(html) {
        return this.isApTagged(html) && this.isAlTagged(html);
    }

    // identical to createMultiLineHtml except it fixes the text wrap issue
    public static createMultiLineHtmlWrapped(topLeft, topRight, bottom) {
        let html: string = "<div class='multiLineDisplay details' style='width:100%'>";
        html += topLeft.trim();
        if (topRight) {
            html += " " + topRight.trim();
        }
        if (bottom) {
            html += "<br>" + bottom.trim();
        }
        html += "</div>";
        return html;
    }

    public static createMultiLineHtml(topLeft, topRight, bottom) {
        let html: string = "<div class='multiLineDisplay'>";
        html += topLeft.trim();
        if (topRight) {
            html += " " + topRight.trim();
        }
        if (bottom) {
            html += "<br>" + bottom.trim();
        }
        html += "</div>";
        return html;
    }

    private static toggleIconHtml =
        `<span class="icon" aria-hidden="true" style="float:right;font-size:20px;margin-top:-4px;">
            <svg class="uitk-icon-svg">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#uitk-icon-toggle90"></use>
            </svg>
        </span>`;
}
